import { createContext, useContext, useMemo } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { preloadPortfolioData, getTranslations } from '../firebase';
import { logError } from '../utils/error-handler';

const TranslationsContext = createContext(null);

export const TranslationsProvider = ({ children }) => {
  const [translations, setTranslations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTranslations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await preloadPortfolioData();
      const translationsData = getTranslations();
      setTranslations(translationsData);
    } catch (err) {
      logError(err, 'TranslationsProvider', {});
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const switchLanguageClientSide = useCallback(() => {
    requestAnimationFrame(() => {
      try {
        const translationsData = getTranslations();
        setTranslations(translationsData);
      } catch (err) {
        logError(err, 'switchLanguageClientSide - Translations', {});
        loadTranslations();
      }
    });
  }, [loadTranslations]);

  useEffect(() => {
    let isMounted = true;
    loadTranslations();

    const handleLanguageChange = () => {
      if (isMounted) {
        switchLanguageClientSide();
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      isMounted = false;
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [loadTranslations, switchLanguageClientSide]);

  const value = useMemo(
    () => ({ translations, loading, error }),
    [translations, loading, error]
  );

  return (
    <TranslationsContext.Provider value={value}>
      {children}
    </TranslationsContext.Provider>
  );
};

export const useTranslations = () => {
  const context = useContext(TranslationsContext);
  if (!context) {
    throw new Error('useTranslations must be used within TranslationsProvider');
  }
  return context;
};
